/* =============================================================================
   Fines Foam — shared front-end helpers.

   Loaded on every page (theme.liquid) BEFORE ff-cart.js and before any section
   script, so everything else can rely on window.FF existing.

   Exposes:
     FF.debounce(fn, wait)              -> debounced fn
     FF.raf(fn)                         -> rAF-throttled fn
     FF.prefersReducedMotion()          -> Boolean (live, not cached)
     FF.focusable(root)                 -> Array of tabbable elements
     FF.trapFocus(container, opener)    -> release() function
     FF.lockScroll(Boolean)             -> body scroll lock (reference counted)
     FF.watchScroll(el, threshold)      -> toggles "is-scrolled" on el
     FF.reveal(root)                    -> wires [data-ff-reveal] inside root
     FF.on / FF.off / FF.emit           -> tiny pub/sub (used by ff-cart.js)
     FF.money(cents)                    -> formats using the shop money format
   ========================================================================== */
(function () {
  'use strict';

  var FF = (window.FF = window.FF || {});

  /* ---------------------------------------------------------------------- */
  /* Timing helpers                                                          */
  /* ---------------------------------------------------------------------- */

  FF.debounce = function (fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait || 250);
    };
  };

  /* Collapses bursts of calls (scroll, resize) into one per animation frame. */
  FF.raf = function (fn) {
    var queued = false;
    return function () {
      var args = arguments;
      var self = this;
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn.apply(self, args);
      });
    };
  };

  FF.prefersReducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------------------------------------------------------------------- */
  /* Focus management                                                        */
  /* ---------------------------------------------------------------------- */

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  FF.focusable = function (root) {
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[inert]')) return false;
      if (el.hasAttribute('hidden')) return false;
      /* Cheap visibility test: no boxes means display:none or a collapsed parent. */
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  };

  /*
    Keeps Tab inside `container` until the returned release() is called.
    `opener` (optional) gets focus back on release, which is what keyboard and
    screen-reader users expect after a dialog closes.
  */
  FF.trapFocus = function (container, opener) {
    if (!container) {
      return function () {};
    }

    var previous = opener || document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;

      var items = FF.focusable(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* Catches focus that lands outside the dialog some other way. */
    function onFocusIn(event) {
      if (container.contains(event.target)) return;
      var items = FF.focusable(container);
      (items[0] || container).focus();
    }

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);

    return function release(restoreFocus) {
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('focusin', onFocusIn);
      if (restoreFocus !== false && previous && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  };

  /* ---------------------------------------------------------------------- */
  /* Scroll lock — reference counted, so the cart drawer and the mobile menu  */
  /* cannot unlock each other.                                               */
  /* ---------------------------------------------------------------------- */

  var lockCount = 0;
  var savedScrollY = 0;

  FF.lockScroll = function (lock) {
    if (lock) {
      lockCount += 1;
      if (lockCount > 1) return;
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = -savedScrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount > 0) return;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Scrolled state                                                          */
  /* ---------------------------------------------------------------------- */

  FF.watchScroll = function (el, threshold) {
    if (!el) {
      return function () {};
    }
    var limit = typeof threshold === 'number' ? threshold : 8;

    var update = FF.raf(function () {
      el.classList.toggle('is-scrolled', window.scrollY > limit);
    });

    update();
    window.addEventListener('scroll', update, { passive: true });

    return function stop() {
      window.removeEventListener('scroll', update);
    };
  };

  /* ---------------------------------------------------------------------- */
  /* Reveal on scroll                                                        */
  /* ---------------------------------------------------------------------- */

  /*
    Adds "is-revealed" to [data-ff-reveal] elements as they enter the viewport.
    Reduced motion (or a browser without IntersectionObserver) reveals
    everything at once, so content is never stuck behind an animation that
    will not run.
  */
  FF.reveal = function (root) {
    var scope = root || document;
    var items = Array.prototype.slice.call(
      scope.querySelectorAll('[data-ff-reveal]:not(.is-revealed)')
    );
    if (!items.length) return;

    if (FF.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Background video — plays only while on screen, and never on reduced      */
  /* motion or a metered connection. Used by the footer cotton-field clip.    */
  /* ---------------------------------------------------------------------- */

  FF.autoplayVideos = function (root) {
    var scope = root || document;
    var videos = Array.prototype.slice.call(
      scope.querySelectorAll('[data-ff-autoplay-video] video')
    );
    if (!videos.length) return;

    var connection = navigator.connection || {};
    var metered = connection.saveData === true || /(^|-)2g/.test(connection.effectiveType || '');
    if (FF.prefersReducedMotion() || metered) return;

    videos.forEach(function (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('playsinline', '');

      function play() {
        if (video.preload !== 'auto') {
          video.preload = 'auto';
          video.load();
        }
        var attempt = video.play();
        if (attempt && attempt.catch) {
          attempt.catch(function () {});
        }
      }

      if (!('IntersectionObserver' in window)) {
        play();
        return;
      }

      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              play();
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: '300px 0px' }
      ).observe(video);
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Pub/sub — ff-cart.js publishes, sections subscribe                      */
  /* ---------------------------------------------------------------------- */

  var subscribers = {};

  FF.on = function (event, handler) {
    (subscribers[event] = subscribers[event] || []).push(handler);
    return function () {
      FF.off(event, handler);
    };
  };

  FF.off = function (event, handler) {
    var list = subscribers[event];
    if (!list) return;
    subscribers[event] = list.filter(function (fn) {
      return fn !== handler;
    });
  };

  FF.emit = function (event, detail) {
    (subscribers[event] || []).slice().forEach(function (handler) {
      /* One broken subscriber must not stop the others — and must not be silent. */
      try {
        handler(detail);
      } catch (error) {
        console.error('[FF] subscriber for "' + event + '" threw:', error);
      }
    });
    document.dispatchEvent(new CustomEvent('ff:' + event, { detail: detail }));
  };

  /* ---------------------------------------------------------------------- */
  /* Money — minimal Shopify money_format renderer.                          */
  /* window.ffMoneyFormat is set by ff-cart-drawer.liquid.                   */
  /* ---------------------------------------------------------------------- */

  FF.money = function (cents) {
    var format = window.ffMoneyFormat || '${{amount}}';
    var value = (Number(cents) || 0) / 100;

    function group(number, decimals, thousands, decimal) {
      var parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal || '.');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return group(value, 0, ',');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, '’');
        default:
          return group(value, 2, ',');
      }
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */

  function init() {
    /* Gate the reveal primitive on JS so no-JS visitors always see content. */
    document.documentElement.classList.add('ff-motion');

    FF.watchScroll(document.documentElement);
    FF.reveal(document);
    FF.autoplayVideos(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor swaps sections in after load, so re-wire what we own. */
  document.addEventListener('shopify:section:load', function (event) {
    FF.reveal(event.target);
    FF.autoplayVideos(event.target);
  });
})();
